import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";

import { Link } from "react-router-dom";
import { dayjs, type FriendDetail } from "@boo/shared";
import { Button } from "@/components/ui/button";
import ConfirmModal from "@/components/ConfirmModal";
import { fetchFriend } from "@/services/friendApi";
import { useFriendsStore } from "../store/useFriendsStore";
import AvatarEditor from "./AvatarEditor";
import { INPUT_CLASS, type AvatarDraft } from "../types/friends.types";

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export default function FriendModal({
  friendId,
  onClose,
}: {
  friendId: string;
  onClose: () => void;
}) {
  const {
    friends,
    editFriend,
    removeFriend,
    mergeInto,
    setEmoji,
    uploadAvatar,
    removeAvatar,
  } = useFriendsStore();
  const [detail, setDetail] = useState<FriendDetail | null>(null);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [avatarDraft, setAvatarDraft] = useState<AvatarDraft>({
    kind: "unchanged",
  });
  const [mergeTarget, setMergeTarget] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<"merge" | "delete" | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const loaded = await fetchFriend(friendId);
        setDetail(loaded);
        setName(loaded.name);
        setContact(loaded.contactInfo ?? "");
      } catch (error) {
        toast.error(errorMessage(error, "Failed to load friend"));
        onClose();
      }
    }
    void load();
  }, [friendId, onClose]);

  const detailsDirty =
    detail !== null &&
    (name.trim() !== detail.name ||
      contact.trim() !== (detail.contactInfo ?? ""));
  const dirty = detailsDirty || avatarDraft.kind !== "unchanged";
  const target = friends.find((f) => f.id === mergeTarget);
  const current = friends.find((f) => f.id === friendId);

  // Nothing is sent until Save: details first, then the staged avatar.
  async function handleSave() {
    if (!detail || !name.trim()) return;
    setSaving(true);
    try {
      if (detailsDirty) {
        const updated = await editFriend(detail.id, {
          name: name.trim(),
          contactInfo: contact.trim() || null,
        });
        setDetail({ ...detail, ...updated });
      }
      if (avatarDraft.kind === "emoji") {
        await setEmoji(detail.id, avatarDraft.emoji);
      } else if (avatarDraft.kind === "photo") {
        await uploadAvatar(detail.id, avatarDraft.blob);
      } else if (avatarDraft.kind === "none") {
        await removeAvatar(detail.id);
      }
      toast.success("Saved");
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save"));
    } finally {
      setSaving(false);
    }
  }

  async function handleMerge() {
    if (!detail || !mergeTarget) return;
    setConfirm(null);
    try {
      await mergeInto(detail.id, mergeTarget);
      toast.success(`Merged into ${target?.name ?? "friend"}`);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to merge"));
    }
  }

  async function handleDelete() {
    if (!detail) return;
    setConfirm(null);
    try {
      await removeFriend(detail.id);
      onClose();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to delete"));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[15px] font-semibold text-foreground">Friend</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {!detail ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <div className="space-y-5">
            {current && (
              <AvatarEditor
                friend={current}
                name={name}
                draft={avatarDraft}
                onChange={setAvatarDraft}
              />
            )}
            <div className="space-y-2">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  className={`${INPUT_CLASS} py-2 text-base text-foreground sm:text-sm`}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Phone / Line ID
                <input
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  maxLength={200}
                  className={`${INPUT_CLASS} py-2 text-base text-foreground sm:text-sm`}
                />
              </label>
              <p className="text-xs text-muted-foreground">
                Renaming doesn&apos;t change names on past bills.
              </p>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={!dirty || !name.trim()}
                loading={saving}
              >
                Save
              </Button>
            </div>

            <div>
              <h3 className="mb-1.5 text-xs font-medium text-muted-foreground">
                Bills ({detail.bills.length})
              </h3>
              {detail.bills.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Not on any bill yet
                </p>
              ) : (
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {detail.bills.map((b) => (
                    <li key={b.id}>
                      <Link
                        to={`/bills/${b.id}`}
                        className="flex min-h-10 items-center justify-between gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent"
                      >
                        <span className="min-w-0 truncate">{b.title}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {dayjs(b.date).format("D MMM YYYY")}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground">
                Merge into…
              </h3>
              <div className="flex gap-2">
                <select
                  aria-label="Merge into"
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  className={`${INPUT_CLASS} min-w-0 flex-1 py-2 text-base sm:text-sm`}
                >
                  <option value="">Choose a friend</option>
                  {friends
                    .filter((f) => f.id !== detail.id)
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!mergeTarget}
                  onClick={() => setConfirm("merge")}
                >
                  Merge
                </Button>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full text-red-500 hover:text-red-600"
              onClick={() => setConfirm("delete")}
            >
              Delete friend
            </Button>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirm === "merge"}
        title="Merge friends?"
        message={`All of ${detail?.name ?? "this friend"}'s bills and groups move to ${target?.name ?? "the other friend"}, and ${detail?.name ?? "this friend"} is deleted.`}
        confirmLabel="Merge"
        danger
        onConfirm={handleMerge}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmModal
        open={confirm === "delete"}
        title="Delete friend?"
        message={`${detail?.name ?? "This friend"} is removed from all split groups. A friend who is on a bill can't be deleted — merge them instead.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
